import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { 
  History, 
  User, 
  MessageSquare, 
  Send, 
  FileText, 
  Globe,
  Calendar,
  DollarSign,
  Home,
  Crown,
  Users,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  XCircle,
  Edit3,
  Clock
} from 'lucide-react';
import { getFieldLabel, formatChangeValue, getChangeCategory } from '@/utils/bookingLogger';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface BookingHistoryTimelineProps {
  booking: BookingWithFinancials;
}

/**
 * 🎨 Этап 6.2: Timeline визуализация истории изменений
 * Вертикальная временная шкала с иконками и группировкой по категориям
 */
export const BookingHistoryTimeline: React.FC<BookingHistoryTimelineProps> = ({ booking }) => {
  // Состояние для имён пользователей
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Собираем уникальные user_id из истории изменений
  const userIds = useMemo(() => {
    const ids = new Set<string>();
    booking.history?.forEach(change => {
      if (change.changed_by) {
        ids.add(change.changed_by);
      }
    });
    return Array.from(ids);
  }, [booking.history]);

  // Загружаем display_name пользователей из user_profiles
  useEffect(() => {
    const loadUserNames = async () => {
      if (userIds.length === 0) return;
      
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('user_id, display_name')
          .in('user_id', userIds);
        
        if (data) {
          const names: Record<string, string> = {};
          data.forEach(profile => {
            names[profile.user_id] = profile.display_name || profile.user_id.slice(0, 8) + '...';
          });
          setUserNames(names);
        }
      } catch (err) {
        console.error('Error loading user names:', err);
      }
    };
    
    loadUserNames();
  }, [userIds]);

  // Функция получения отображаемого имени пользователя
  const getUserDisplayName = (userId: string) => {
    return userNames[userId] || userId.slice(0, 8) + '...';
  };

  // Иконки для категорий изменений
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'guest_info':
        return <User className="h-4 w-4" />;
      case 'dates':
        return <Calendar className="h-4 w-4" />;
      case 'room':
        return <Home className="h-4 w-4" />;
      case 'financial':
        return <DollarSign className="h-4 w-4" />;
      case 'services':
        return <MapPin className="h-4 w-4" />;
      case 'vip':
        return <Crown className="h-4 w-4" />;
      case 'status':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'communication':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Edit3 className="h-4 w-4" />;
    }
  };

  // Цвета для категорий
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'guest_info':
        return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800';
      case 'dates':
        return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-400 dark:border-purple-800';
      case 'room':
        return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800';
      case 'financial':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-400 dark:border-yellow-800';
      case 'services':
        return 'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-950 dark:text-pink-400 dark:border-pink-800';
      case 'vip':
        return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800';
      case 'status':
        return 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-400 dark:border-teal-800';
      case 'communication':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-800';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-950 dark:text-gray-400 dark:border-gray-800';
    }
  };

  // Группируем изменения по дате И СОРТИРУЕМ В ОБРАТНОМ ПОРЯДКЕ (самые свежие сверху)
  const groupedHistory = useMemo(() => {
    if (!booking.history || booking.history.length === 0) return {};
    
    // Сначала сортируем все изменения по дате (самые свежие первые)
    const sortedHistory = [...booking.history].sort((a, b) => 
      new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
    );
    
    const groups: Record<string, typeof booking.history> = {};
    
    sortedHistory.forEach(change => {
      const date = new Date(change.changed_at).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(change);
    });
    
    return groups;
  }, [booking.history]);

  const isWhatsAppInvoice = (field: string) => field === 'whatsapp_invoice_sent';

  const isFinancialField = (field: string) => {
    return ['total_amount', 'discount_amount', 'paid_amount', 'balance_amount', 'advance_payment'].includes(field);
  };

  const isDateField = (field: string) => {
    return field.includes('date') || field.includes('_at');
  };

  const formatHistoryValue = (value: any, field: string) => {
    if (value === null || value === undefined) return 'Пусто';
    
    // Если это объект с booking_snapshot (контекстные данные)
    if (typeof value === 'object' && value !== null && 'booking_snapshot' in value) {
      // Возвращаем только значение поля, snapshot покажем отдельно
      return formatHistoryValue(value.field_value, field);
    }
    
    // 🔥 НОВОЕ: Если это объект с полем value (из триггера audit_booking_price_change)
    if (typeof value === 'object' && value !== null && 'value' in value) {
      return formatHistoryValue(value.value, field);
    }
    
    // Форматирование дат
    if (isDateField(field) && typeof value === 'string') {
      try {
        const date = new Date(value);
        return date.toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return formatChangeValue(value, field);
      }
    }
    
    // Форматирование финансовых полей
    if (isFinancialField(field) && typeof value === 'number') {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0
      }).format(value);
    }
    
    return formatChangeValue(value, field);
  };

  const getBookingSnapshot = (value: any) => {
    if (typeof value === 'object' && value !== null && 'booking_snapshot' in value) {
      return value.booking_snapshot;
    }
    return null;
  };

  if (!booking.history || booking.history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            История изменений
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-12">
            <History className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">История изменений пуста</p>
            <p className="text-sm">Все изменения бронирования будут отображаться здесь</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          История изменений
          <Badge variant="secondary" className="ml-auto">
            {booking.history.length} {booking.history.length === 1 ? 'изменение' : 'изменений'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {Object.entries(groupedHistory).map(([date, changes]) => (
            <div key={date} className="space-y-4">
              {/* Дата группы */}
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  {date}
                </h3>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Timeline изменений */}
              <div className="relative pl-8 space-y-6">
                {/* Вертикальная линия */}
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-border" />

                {changes.map((change, index) => {
                  const category = getChangeCategory(change.field_changed);
                  const categoryColor = getCategoryColor(category);
                  const categoryIcon = getCategoryIcon(category);
                  const isLast = index === changes.length - 1;
                  const isWhatsApp = isWhatsAppInvoice(change.field_changed);
                  const snapshot = getBookingSnapshot(change.new_value);

                  return (
                    <div key={change.id} className="relative">
                      {/* Точка на timeline */}
                      <div className={cn(
                        "absolute -left-[1.6rem] top-2 w-6 h-6 rounded-full border-2 flex items-center justify-center",
                        categoryColor,
                        "shadow-md"
                      )}>
                        {categoryIcon}
                      </div>

                      {/* Карточка изменения */}
                      <div className={cn(
                        "rounded-lg border-2 p-4 bg-card transition-all hover:shadow-md",
                        isWhatsApp && "border-emerald-300 dark:border-emerald-700 bg-gradient-to-br from-emerald-50/50 to-green-50/30 dark:from-emerald-950/20 dark:to-green-950/10"
                      )}>
                        {/* Header изменения */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">
                              {getFieldLabel(change.field_changed)}
                            </span>
                            {!isWhatsApp && (
                              <Badge variant="outline" className={cn("text-xs", categoryColor)}>
                                {category.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(change.changed_at).toLocaleTimeString('ru-RU', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>

                        {/* WhatsApp специальное отображение */}
                        {isWhatsApp && change.new_value ? (
                          <div className="space-y-3 bg-background/80 backdrop-blur-sm p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                <span className="text-muted-foreground">Счёт:</span>
                                <span className="font-semibold">{change.new_value.invoice_number}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                <span className="text-muted-foreground">Язык:</span>
                                <span className="font-medium">
                                  {change.new_value.language === 'ru' ? '🇷🇺 Русский' : '🇬🇧 English'}
                                </span>
                              </div>
                            </div>
                            <div className="border-t border-emerald-200 dark:border-emerald-800 pt-3 grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground block">Сумма:</span>
                                <span className="font-semibold">
                                  {change.new_value.total_amount?.toLocaleString()} ₽
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block">Оплачено:</span>
                                <span className="font-medium text-green-600 dark:text-green-400">
                                  {change.new_value.paid_amount?.toLocaleString()} ₽
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block">К оплате:</span>
                                <span className="font-medium text-orange-600 dark:text-orange-400">
                                  {change.new_value.balance_amount?.toLocaleString()} ₽
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Обычное отображение изменений */
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <div className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                                  <XCircle className="h-3 w-3" />
                                  Было:
                                </div>
                                <div className={cn(
                                  "p-3 rounded border text-sm font-mono",
                                  isFinancialField(change.field_changed)
                                    ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 font-semibold"
                                    : "bg-muted/50"
                                )}>
                                  {formatHistoryValue(change.old_value, change.field_changed)}
                                </div>
                              </div>
                              <div className="space-y-1">
                                <div className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                                  Стало:
                                </div>
                                <div className={cn(
                                  "p-3 rounded border text-sm font-mono",
                                  isFinancialField(change.field_changed)
                                    ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 font-semibold"
                                    : "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                                )}>
                                  {formatHistoryValue(change.new_value, change.field_changed)}
                                </div>
                              </div>
                            </div>
                            
                            {/* Дата и время изменения */}
                            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
                              <Clock className="h-3 w-3" />
                              <span>Точное время:</span>
                              <span className="font-medium">
                                {new Date(change.changed_at).toLocaleString('ru-RU', {
                                  day: '2-digit',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </span>
                            </div>
                            
                            {/* Контекстные данные брони (snapshot) */}
                            {snapshot && (
                              <div className="mt-3 pt-3 border-t bg-blue-50/50 dark:bg-blue-950/10 p-3 rounded-lg">
                                <div className="text-xs font-medium text-muted-foreground mb-2">
                                  📋 Состояние брони на момент изменения:
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {snapshot.check_in_date && (
                                    <div>
                                      <span className="text-muted-foreground">Заезд:</span>
                                      <span className="font-medium ml-2">
                                        {new Date(snapshot.check_in_date).toLocaleDateString('ru-RU')}
                                      </span>
                                    </div>
                                  )}
                                  {snapshot.check_out_date && (
                                    <div>
                                      <span className="text-muted-foreground">Выезд:</span>
                                      <span className="font-medium ml-2">
                                        {new Date(snapshot.check_out_date).toLocaleDateString('ru-RU')}
                                      </span>
                                    </div>
                                  )}
                                  {snapshot.room_type && (
                                    <div className="col-span-2">
                                      <span className="text-muted-foreground">Номер:</span>
                                      <span className="font-medium ml-2">
                                        {snapshot.room_type}
                                        {snapshot.room_number && (
                                          <span className="ml-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-semibold">
                                            № {snapshot.room_number}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {snapshot.total_amount !== undefined && (
                                    <div>
                                      <span className="text-muted-foreground">Сумма:</span>
                                      <span className="font-semibold ml-2">
                                        {new Intl.NumberFormat('ru-RU', {
                                          style: 'currency',
                                          currency: 'RUB',
                                          minimumFractionDigits: 0
                                        }).format(snapshot.total_amount)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Причина изменения */}
                        {change.change_reason && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="text-xs text-muted-foreground mb-1 font-medium">
                              Причина:
                            </div>
                            <div className="text-sm bg-blue-50 dark:bg-blue-950/20 p-2 rounded border border-blue-200 dark:border-blue-800">
                              {change.change_reason}
                            </div>
                          </div>
                        )}

                        {/* Кто изменил */}
                        {change.changed_by && (
                          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span>Изменено: {getUserDisplayName(change.changed_by)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
